import { BaseUniDriver } from '@wix/wix-ui-test-utils/base-driver';
import { baseUniDriverFactory } from '../../testkitUtils/unidriver';
import { Simulate } from 'react-dom/test-utils';
import { UniDriver } from '@wix/wix-ui-test-utils/unidriver';
import { CHECKBOX_DATA_HOOKS, CHEKCBOX_DATA_KEYS } from './dataHooks';
import {
  IconTooltipDriver,
  iconTooltipDriverFactory,
} from '../IconTooltip/IconTooltip.driver';
import { errorMessageWrapperDriverFactory } from '../internal/ErrorMessageWrapper/ErrorMessageWrapper.driver';
import { dataHook } from '../../testkitUtils/utils';
import { actDelay } from '../../test/helpers';

export interface CheckboxDriver extends BaseUniDriver {
  errorIcon: IconTooltipDriver;
  hasError(): Promise<boolean>;
  getErrorMessage(): Promise<string>;
  hasDisabled(): Promise<boolean>;
  hasIndeterminate(): Promise<boolean>;
  hasChecked(): Promise<boolean>;
  clickOnCheckbox(): Promise<void>;
  getLabel(): UniDriver<HTMLLabelElement>;
  hoverCheckbox(): Promise<void>;
  getBorderColor(): Promise<string>;
  getText(): Promise<string>;
  isErrorTooltipShown(): Promise<boolean>;
  clickOnErrorIcon(): Promise<void>;
  getCheckboxAriaDescribedby(): Promise<string>;
  getCheckboxAriaLabel(): Promise<string>;
  getCheckboxLang(): Promise<string>;
  isFocused(): Promise<boolean>;
  isRequired(): Promise<boolean>;
}

export const checkboxDriverFactory = (
  base: UniDriver,
  body: UniDriver,
): CheckboxDriver => {
  const checkboxCoreDatahook = dataHook(CHECKBOX_DATA_HOOKS.CheckboxCore);
  const checkboxIconDatahook = dataHook(CHECKBOX_DATA_HOOKS.IconWrapper);
  const errorIconDatahook = dataHook(CHECKBOX_DATA_HOOKS.ErrorIcon);
  const labelDatahook = dataHook(CHECKBOX_DATA_HOOKS.LabelWrapper);
  const getLabel = (): UniDriver<HTMLLabelElement> =>
    base.$(`${labelDatahook}`);
  const getInput = (): UniDriver<HTMLInputElement> =>
    base.$(`${checkboxCoreDatahook} input`);
  const getCheckboxIcon = (): UniDriver => base.$(`${checkboxIconDatahook}`);
  const errorIconDriver = iconTooltipDriverFactory(
    base.$(errorIconDatahook),
    body,
  );

  return {
    ...baseUniDriverFactory(base),
    errorIcon: errorIconDriver,
    getLabel,
    async hasError() {
      // old error message
      const hasError = (await base.attr(CHEKCBOX_DATA_KEYS.Error)) === 'true';

      if (hasError) {
        return true;
      }

      // new error message
      const errorContainer = base.$(`[${CHEKCBOX_DATA_KEYS.Error}="true"]`);
      return errorContainer.exists();
    },
    async getErrorMessage() {
      if (await errorIconDriver.exists()) {
        return errorIconDriver.getTooltipContent().text();
      } else {
        const errorMessageWrapperDriver =
          errorMessageWrapperDriverFactory(base);
        if (await errorMessageWrapperDriver.exists()) {
          return errorMessageWrapperDriver.getErrorMessage();
        }
      }
      throw new Error('Cannot find error message in checkbox');
    },
    async hasDisabled() {
      return (await base.attr(CHEKCBOX_DATA_KEYS.Disabled)) === 'true';
    },
    async hasIndeterminate() {
      return (await base.attr(CHEKCBOX_DATA_KEYS.Indeterminate)) === 'true';
    },
    async hasChecked() {
      return (await base.attr(CHEKCBOX_DATA_KEYS.Checked)) === 'true';
    },
    async clickOnCheckbox() {
      const inputNative = await getInput().getNative();
      return Simulate.change(inputNative);
    },
    async hoverCheckbox() {
      await getCheckboxIcon().hover();
      // wait for transition to end
      await actDelay(200);
    },
    async getBorderColor() {
      const icon = getCheckboxIcon();
      const native = await icon.getNative();

      return native.getCssValue('border-color');
    },
    async isErrorTooltipShown() {
      return errorIconDriver.tooltipExists();
    },
    async clickOnErrorIcon() {
      return errorIconDriver.click();
    },
    async getCheckboxAriaDescribedby() {
      return getInput().attr('aria-describedby');
    },
    async getCheckboxAriaLabel() {
      return getInput().attr('aria-label');
    },
    async getCheckboxLang() {
      return base.attr('lang');
    },
    async isFocused() {
      return (await base.attr(CHEKCBOX_DATA_KEYS.Focused)) === 'true';
    },
    async isRequired() {
      return (await getInput().attr('required')) !== null;
    },
    async getText() {
      return base.text();
    },
  };
};
